import { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'gestaki:pwa-dismissed-until';
const DISMISS_DAYS = 14;

export function PWAInstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function shouldShow(): boolean {
      try {
        const dismissedUntil = localStorage.getItem(STORAGE_KEY);
        if (!dismissedUntil) return true;
        return Date.now() > Number(dismissedUntil);
      } catch {
        return true;
      }
    }

    function onBeforeInstall(e: Event) {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setEvent(promptEvent);
      if (shouldShow()) {
        // Espera um pouco pra não atropelar o login/onboarding
        setTimeout(() => setVisible(true), 3000);
      }
    }

    function onInstalled() {
      setEvent(null);
      setVisible(false);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function install() {
    if (!event) return;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === 'dismissed') {
        dismiss();
      }
    } catch {
      // user cancelled
    }
    setVisible(false);
    setEvent(null);
  }

  function dismiss() {
    setVisible(false);
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(STORAGE_KEY, String(until));
    } catch {
      /* ignore */
    }
  }

  if (!visible || !event) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50 w-[min(94vw,360px)]',
        'rounded-3 border border-card-stroke bg-card shadow-clay p-4',
        'animate-slide-in-right',
      )}
      role="dialog"
      aria-label="Instalar GestAKI"
    >
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2 bg-mint-soft text-mint-deep">
          <Smartphone className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-[14px] font-bold tracking-[-0.01em] text-fg">
            Instalar GestAKI
          </p>
          <p className="text-xs text-fg-muted mt-0.5">
            Acesso rápido, notificações de prestação e funcionamento offline parcial.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft transition-colors"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={dismiss}
          className="text-xs text-fg-muted hover:text-fg px-2 py-1.5"
        >
          Agora não
        </button>
        <button
          type="button"
          onClick={install}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-xs font-semibold',
            'text-[#FBF7EF] bg-[linear-gradient(160deg,#6FAE93,var(--mint-deep))]',
            'shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_4px_10px_-4px_rgba(40,90,70,0.40)]',
            'hover:brightness-105 transition-all',
          )}
        >
          <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
          Instalar
        </button>
      </div>
    </div>
  );
}
